import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const D2Logo = ({ width = 190, height = 40 }) => (
  <svg width={width} height={height} viewBox="0 0 190 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Document body */}
    <path d="M4 2 L28 2 L38 12 L38 38 L4 38 Z" stroke="#6495ED" strokeWidth="1.5" fill="rgba(100,149,237,0.07)" strokeLinejoin="round"/>
    {/* Folded corner */}
    <path d="M28 2 L28 12 L38 12" stroke="#6495ED" strokeWidth="1" fill="none" strokeLinejoin="round" opacity="0.5"/>
    {/* Passive scan lines */}
    <line x1="9" y1="20" x2="33" y2="20" stroke="#6495ED" strokeWidth="0.7" opacity="0.2" strokeLinecap="round"/>
    <line x1="9" y1="26" x2="33" y2="26" stroke="#6495ED" strokeWidth="0.7" opacity="0.12" strokeLinecap="round"/>
    {/* Active scan line */}
    <line x1="4" y1="20" x2="38" y2="20" stroke="#6495ED" strokeWidth="2" opacity="0.9" strokeLinecap="round"/>
    {/* Cert badge */}
    <circle cx="30" cy="34" r="9" fill="#0a0a0b" stroke="#6495ED" strokeWidth="1.4"/>
    <path d="M25.5 34 L28.5 37 L34.5 30" stroke="#6495ED" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    {/* Wordmark */}
    <text x="46" y="24" fontFamily="'Bebas Neue', sans-serif" fontSize="22" fill="#f0f2f6" letterSpacing="1.5">
      RECERT<tspan fill="#6495ED">AGENT</tspan>
    </text>
    <text x="47" y="35" fontFamily="'DM Sans', sans-serif" fontSize="7" fill="#9aa0ae" letterSpacing="1" textLength="140" lengthAdjust="spacingAndGlyphs">
      BUILDING RECERTIFICATION PLATFORM
    </text>
  </svg>
);

const LandingPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const reveals = document.querySelectorAll('.lp-reveal');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.classList.add('lp-visible');
          }, 80);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    reveals.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&family=DM+Mono:wght@300;400&display=swap');

        .lp-root {
          --black: #0a0a0b;
          --deep: #111214;
          --surface: #16181c;
          --card: #1c1f25;
          --border: rgba(255,255,255,0.07);
          --border-hover: rgba(100,149,237,0.35);
          --silver-light: #d0d4dc;
          --silver: #9aa0ae;
          --silver-dim: #5a6070;
          --blue: #6495ED;
          --blue-bright: #7aaef5;
          --blue-dim: rgba(100,149,237,0.15);
          --blue-glow: rgba(100,149,237,0.08);
          --white: #f0f2f6;
          background: var(--black);
          color: var(--white);
          font-family: 'DM Sans', sans-serif;
          font-weight: 300;
          line-height: 1.6;
          overflow-x: hidden;
          min-height: 100vh;
        }

        .lp-root *, .lp-root *::before, .lp-root *::after {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        .lp-grid-bg {
          position: fixed; inset: 0;
          background-image:
            linear-gradient(rgba(100,149,237,0.015) 1px, transparent 1px),
            linear-gradient(90deg, rgba(100,149,237,0.015) 1px, transparent 1px);
          background-size: 60px 60px;
          pointer-events: none; z-index: 0;
        }

        .lp-ambient {
          position: fixed; width: 600px; height: 600px; border-radius: 50%;
          background: radial-gradient(circle, rgba(100,149,237,0.06) 0%, transparent 70%);
          top: -100px; right: -100px; pointer-events: none; z-index: 0;
        }

        .lp-ambient-2 {
          position: fixed; width: 400px; height: 400px; border-radius: 50%;
          background: radial-gradient(circle, rgba(100,149,237,0.04) 0%, transparent 70%);
          bottom: 20%; left: -50px; pointer-events: none; z-index: 0;
        }

        /* NAV */
        .lp-nav {
          position: fixed; top: 0; left: 0; right: 0; z-index: 100;
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 48px; height: 64px;
          background: rgba(10,10,11,0.85); backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255,255,255,0.07);
        }

        .lp-nav-logo { display: flex; align-items: center; gap: 10px; text-decoration: none; cursor: pointer; }

        .lp-nav-links { display: flex; align-items: center; gap: 32px; list-style: none; }
        .lp-nav-links a { color: #9aa0ae; text-decoration: none; font-size: 13px; font-weight: 400; letter-spacing: 0.04em; transition: color 0.2s; }
        .lp-nav-links a:hover { color: #f0f2f6; }

        .lp-nav-cta { background: #6495ED !important; color: #0a0a0b !important; padding: 8px 20px; border-radius: 4px; font-weight: 500 !important; transition: background 0.2s !important; }
        .lp-nav-cta:hover { background: #7aaef5 !important; }

        /* HERO */
        .lp-hero {
          position: relative; z-index: 1; min-height: 100vh;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 120px 48px 80px; text-align: center;
        }

        .lp-hero-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(100,149,237,0.15); border: 1px solid rgba(100,149,237,0.25);
          border-radius: 20px; padding: 5px 14px 5px 8px;
          font-size: 11px; font-weight: 500; letter-spacing: 0.08em;
          color: #6495ED; text-transform: uppercase; margin-bottom: 36px;
          animation: lpFadeUp 0.6s ease both;
        }

        .lp-badge-dot { width: 6px; height: 6px; border-radius: 50%; background: #6495ED; animation: lpPulse 2s ease-in-out infinite; }

        @keyframes lpPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }

        .lp-hero-headline {
          font-family: 'Bebas Neue', sans-serif;
          font-size: clamp(72px, 10vw, 140px);
          line-height: 0.92; letter-spacing: 0.02em;
          color: #f0f2f6; margin-bottom: 8px;
          animation: lpFadeUp 0.6s ease 0.1s both;
          width: 100%; overflow: visible;
        }

        .lp-hero-headline .lp-accent { color: #6495ED; display: block; }

        /* ── MOBILE HEADLINE FIX — scales font so RECERTIFICATION never clips ── */
        @media (max-width: 480px) {
          .lp-hero-headline {
            font-size: clamp(52px, 13.5vw, 72px);
            letter-spacing: 0.01em;
          }
          .lp-hero { padding: 100px 20px 60px; }
        }

        .lp-hero-sub {
          font-size: clamp(14px, 2vw, 17px); font-weight: 300; color: #9aa0ae;
          max-width: 520px; margin: 24px auto 48px; line-height: 1.7;
          animation: lpFadeUp 0.6s ease 0.2s both;
        }

        .lp-hero-actions { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; animation: lpFadeUp 0.6s ease 0.3s both; }

        @keyframes lpFadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* BUTTONS */
        .lp-btn-primary {
          background: #6495ED; color: #0a0a0b; border: none;
          padding: 14px 32px; border-radius: 4px;
          font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 500; letter-spacing: 0.03em;
          cursor: pointer; text-decoration: none; display: inline-block;
          transition: background 0.2s, transform 0.15s;
        }
        .lp-btn-primary:hover { background: #7aaef5; transform: translateY(-1px); }

        .lp-btn-secondary {
          background: transparent; color: #d0d4dc; border: 1px solid rgba(100,149,237,0.35);
          padding: 14px 32px; border-radius: 4px;
          font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 400; letter-spacing: 0.03em;
          cursor: pointer; text-decoration: none; display: inline-block;
          transition: border-color 0.2s, color 0.2s, transform 0.15s;
        }
        .lp-btn-secondary:hover { border-color: #6495ED; color: #f0f2f6; transform: translateY(-1px); }

        /* COUNTY STRIP */
        .lp-county-strip {
          position: relative; z-index: 1;
          border-top: 1px solid rgba(255,255,255,0.07); border-bottom: 1px solid rgba(255,255,255,0.07);
          padding: 16px 48px; background: rgba(255,255,255,0.015);
          display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap;
        }

        .lp-county-label { font-size: 10px; font-weight: 500; letter-spacing: 0.12em; color: #5a6070; text-transform: uppercase; margin-right: 8px; }

        .lp-county-pill {
          display: inline-flex; align-items: center; gap: 6px;
          background: #1c1f25; border: 1px solid rgba(255,255,255,0.07);
          border-radius: 3px; padding: 5px 12px;
          font-size: 11px; font-weight: 500; letter-spacing: 0.05em; color: #9aa0ae; text-transform: uppercase;
        }
        .lp-county-pill::before { content: ''; width: 5px; height: 5px; border-radius: 50%; background: #6495ED; opacity: 0.7; }

        /* SECTION */
        .lp-section { position: relative; z-index: 1; padding: 100px 48px; max-width: 1200px; margin: 0 auto; }

        .lp-section-label {
          font-size: 10px; font-weight: 500; letter-spacing: 0.16em;
          color: #6495ED; text-transform: uppercase; margin-bottom: 16px;
          display: flex; align-items: center; gap: 10px;
        }
        .lp-section-label::before { content: ''; display: inline-block; width: 24px; height: 1px; background: #6495ED; }

        .lp-section-title { font-family: 'Bebas Neue', sans-serif; font-size: clamp(36px, 5vw, 64px); letter-spacing: 0.03em; line-height: 1; color: #f0f2f6; margin-bottom: 20px; }
        .lp-section-body { font-size: 15px; color: #9aa0ae; max-width: 540px; line-height: 1.75; }

        /* WORKFLOW */
        .lp-workflow {
          display: grid; grid-template-columns: repeat(3, 1fr);
          gap: 1px; background: rgba(255,255,255,0.07);
          border: 1px solid rgba(255,255,255,0.07); border-radius: 8px; overflow: hidden; margin-top: 64px;
        }

        .lp-step { background: #1c1f25; padding: 40px 36px; transition: background 0.3s; }
        .lp-step:hover { background: #21252e; }
        .lp-step-num { font-family: 'DM Mono', monospace; font-size: 11px; font-weight: 300; color: #6495ED; letter-spacing: 0.1em; margin-bottom: 20px; }
        .lp-step-icon { width: 40px; height: 40px; margin-bottom: 20px; }
        .lp-step-title { font-family: 'Bebas Neue', sans-serif; font-size: 22px; letter-spacing: 0.05em; color: #f0f2f6; margin-bottom: 12px; }
        .lp-step-body { font-size: 13px; color: #9aa0ae; line-height: 1.7; }
        .lp-step-tag {
          display: inline-block; margin-top: 16px;
          background: rgba(100,149,237,0.15); border: 1px solid rgba(100,149,237,0.2);
          border-radius: 3px; padding: 3px 9px;
          font-size: 10px; font-weight: 500; letter-spacing: 0.08em; color: #6495ED; text-transform: uppercase;
        }

        /* FEATURES */
        .lp-features-grid {
          display: grid; grid-template-columns: repeat(2, 1fr);
          gap: 1px; background: rgba(255,255,255,0.07);
          border: 1px solid rgba(255,255,255,0.07); border-radius: 8px; overflow: hidden; margin-top: 64px;
        }

        .lp-feature-card { background: #1c1f25; padding: 36px; transition: background 0.3s; }
        .lp-feature-card:last-child:nth-child(odd) { grid-column: 1 / -1; }
        .lp-feature-card:hover { background: #21252e; }
        .lp-feature-icon { width: 36px; height: 36px; margin-bottom: 20px; }
        .lp-feature-title { font-weight: 500; font-size: 16px; color: #f0f2f6; margin-bottom: 10px; letter-spacing: -0.01em; }
        .lp-feature-body { font-size: 13px; color: #9aa0ae; line-height: 1.7; }

        /* COMPLIANCE STRIP */
        .lp-compliance-strip {
          position: relative; z-index: 1; background: #16181c;
          border-top: 1px solid rgba(255,255,255,0.07); border-bottom: 1px solid rgba(255,255,255,0.07);
          padding: 48px; display: flex; align-items: center; justify-content: space-between; gap: 40px; flex-wrap: wrap;
        }

        .lp-compliance-item { display: flex; align-items: center; gap: 14px; }
        .lp-compliance-icon-wrap {
          width: 44px; height: 44px; border-radius: 6px;
          background: rgba(100,149,237,0.15); border: 1px solid rgba(100,149,237,0.2);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .lp-compliance-text strong { display: block; font-size: 14px; font-weight: 500; color: #f0f2f6; margin-bottom: 2px; }
        .lp-compliance-text span { font-size: 12px; color: #9aa0ae; }

        /* TIERS */
        .lp-tiers-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-top: 64px; }

        .lp-tier-card {
          background: #1c1f25; border: 1px solid rgba(255,255,255,0.07);
          border-radius: 8px; padding: 36px; position: relative;
          transition: border-color 0.3s, transform 0.2s;
        }
        .lp-tier-card:hover { border-color: rgba(100,149,237,0.35); transform: translateY(-2px); }
        .lp-tier-card.lp-featured { border-color: rgba(100,149,237,0.4); background: linear-gradient(160deg, #1e2330 0%, #1c1f25 100%); }

        .lp-tier-badge {
          position: absolute; top: -1px; right: 20px;
          background: #6495ED; color: #0a0a0b;
          font-size: 10px; font-weight: 500; letter-spacing: 0.08em;
          text-transform: uppercase; padding: 4px 10px; border-radius: 0 0 4px 4px;
        }

        .lp-tier-name { font-family: 'Bebas Neue', sans-serif; font-size: 28px; letter-spacing: 0.06em; color: #f0f2f6; margin-bottom: 6px; }
        .lp-tier-tagline { font-size: 12px; color: #9aa0ae; margin-bottom: 28px; }
        .lp-tier-price { font-family: 'DM Mono', monospace; font-size: 13px; color: #5a6070; margin-bottom: 28px; padding-bottom: 28px; border-bottom: 1px solid rgba(255,255,255,0.07); }
        .lp-tier-features { list-style: none; display: flex; flex-direction: column; gap: 10px; margin-bottom: 28px; }
        .lp-tier-features li { display: flex; align-items: flex-start; gap: 10px; font-size: 13px; color: #9aa0ae; line-height: 1.5; }
        .lp-tier-features li::before { content: ''; width: 4px; height: 4px; border-radius: 50%; background: #6495ED; margin-top: 7px; flex-shrink: 0; }

        /* CTA */
        .lp-cta-section { position: relative; z-index: 1; padding: 120px 48px; text-align: center; border-top: 1px solid rgba(255,255,255,0.07); }
        .lp-cta-section::before { content: ''; position: absolute; inset: 0; background: radial-gradient(ellipse 80% 60% at 50% 100%, rgba(100,149,237,0.05) 0%, transparent 70%); pointer-events: none; }
        .lp-cta-headline { font-family: 'Bebas Neue', sans-serif; font-size: clamp(48px, 7vw, 100px); letter-spacing: 0.03em; line-height: 0.95; color: #f0f2f6; margin-bottom: 24px; }
        .lp-cta-sub { font-size: 15px; color: #9aa0ae; max-width: 440px; margin: 0 auto 48px; line-height: 1.7; }

        /* FOOTER */
        .lp-footer { position: relative; z-index: 1; border-top: 1px solid rgba(255,255,255,0.07); padding: 32px 48px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px; }
        .lp-footer-copy { font-size: 12px; color: #5a6070; margin-top: 10px; }
        .lp-footer-links { display: flex; gap: 24px; list-style: none; }
        .lp-footer-links a { font-size: 12px; color: #5a6070; text-decoration: none; transition: color 0.2s; }
        .lp-footer-links a:hover { color: #9aa0ae; }

        /* DIVIDER */
        .lp-divider { position: relative; z-index: 1; height: 1px; background: rgba(255,255,255,0.07); }

        /* REVEAL */
        .lp-reveal { opacity: 0; transform: translateY(24px); transition: opacity 0.6s ease, transform 0.6s ease; }
        .lp-visible { opacity: 1; transform: translateY(0); }

        @media (max-width: 768px) {
          .lp-nav { padding: 0 24px; }
          .lp-nav-links { gap: 0; }
          .lp-nav-links li:not(.lp-nav-signin) { display: none; }
          .lp-section { padding: 64px 24px; }
          .lp-workflow { grid-template-columns: 1fr; }
          .lp-features-grid { grid-template-columns: 1fr; }
          .lp-tiers-grid { grid-template-columns: 1fr; }
          .lp-compliance-strip { padding: 32px 24px; flex-direction: column; }
          .lp-hero { padding: 100px 24px 60px; }
          .lp-cta-section { padding: 80px 24px; }
          .lp-footer { padding: 24px; flex-direction: column; align-items: flex-start; }
        }
      `}</style>

      <div className="lp-root">
        <div className="lp-grid-bg" />
        <div className="lp-ambient" />
        <div className="lp-ambient-2" />

        {/* NAV */}
        <nav className="lp-nav">
          <div className="lp-nav-logo" onClick={() => window.scrollTo(0, 0)}>
            <D2Logo width={190} height={40} />
          </div>
          <ul className="lp-nav-links">
            <li><a href="#workflow">Workflow</a></li>
            <li><a href="#features">Features</a></li>
            <li><a href="#compliance">Compliance</a></li>
            <li><a href="#pricing">Pricing</a></li>
            <li className="lp-nav-signin"><Link to="/login" className="lp-nav-cta">Sign In</Link></li>
          </ul>
        </nav>

        {/* HERO */}
        <div className="lp-hero">
          <div className="lp-hero-badge">
            <span className="lp-badge-dot" />
            Florida Building Safety — BSIP Compliant
          </div>
          <h1 className="lp-hero-headline">
            Building<br />
            <span className="lp-accent">Recertification</span><br />
            Reimagined
          </h1>
          <p className="lp-hero-sub">
            End-to-end platform for licensed engineers. Automate intake, field documentation, and county-ready PDF generation.
          </p>
          <div className="lp-hero-actions">
            <a href="#workflow" className="lp-btn-primary">See the Workflow</a>
            <a href="#pricing" className="lp-btn-secondary">View Pricing</a>
          </div>
        </div>

        {/* COUNTY STRIP */}
        <div className="lp-county-strip">
          <span className="lp-county-pill">Broward County</span>
          <span className="lp-county-pill">Miami-Dade County</span>
          <span className="lp-county-pill">Palm Beach County</span>
        </div>

        {/* WORKFLOW */}
        <section className="lp-section" id="workflow">
          <div className="lp-section-label">How It Works</div>
          <h2 className="lp-section-title lp-reveal">Three steps.<br />Zero redundancy.</h2>
          <p className="lp-section-body lp-reveal">From first intake to final submission — every step of the recertification workflow connected in one platform.</p>
          <div className="lp-workflow lp-reveal">
            <div className="lp-step">
              <div className="lp-step-num">01 / INTAKE</div>
              <svg className="lp-step-icon" viewBox="0 0 40 40" fill="none">
                <rect x="4" y="8" width="24" height="28" rx="3" stroke="#6495ED" strokeWidth="1.2"/>
                <line x1="10" y1="16" x2="22" y2="16" stroke="#6495ED" strokeWidth="1.2" strokeLinecap="round"/>
                <line x1="10" y1="21" x2="22" y2="21" stroke="#6495ED" strokeWidth="1.2" strokeLinecap="round"/>
                <line x1="10" y1="26" x2="17" y2="26" stroke="#6495ED" strokeWidth="1.2" strokeLinecap="round"/>
                <circle cx="30" cy="30" r="8" fill="#0a0a0b" stroke="#6495ED" strokeWidth="1.2"/>
                <path d="M27 30 L29.5 32.5 L33 28" stroke="#6495ED" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="lp-step-title">Project Intake</div>
              <div className="lp-step-body">Auto-populate building data from county property appraiser APIs. Establish the project record on the cloud server in under two minutes.</div>
              <span className="lp-step-tag">Web App</span>
            </div>
            <div className="lp-step">
              <div className="lp-step-num">02 / FIELD</div>
              <svg className="lp-step-icon" viewBox="0 0 40 40" fill="none">
                <rect x="10" y="4" width="20" height="32" rx="4" stroke="#6495ED" strokeWidth="1.2"/>
                <circle cx="20" cy="28" r="3" stroke="#6495ED" strokeWidth="1.2"/>
                <line x1="16" y1="9" x2="24" y2="9" stroke="#6495ED" strokeWidth="1.2" strokeLinecap="round"/>
                <circle cx="15" cy="16" r="5" stroke="#6495ED" strokeWidth="1.2"/>
                <path d="M15 13 L15 19 M12 16 L18 16" stroke="#6495ED" strokeWidth="1" strokeLinecap="round"/>
              </svg>
              <div className="lp-step-title">Field Documentation</div>
              <div className="lp-step-body">Photograph deficiencies with the iOS app. Attach photos to specific form line items with captions — synced to the cloud in real time.</div>
              <span className="lp-step-tag">iOS App</span>
            </div>
            <div className="lp-step">
              <div className="lp-step-num">03 / REPORT</div>
              <svg className="lp-step-icon" viewBox="0 0 40 40" fill="none">
                <rect x="6" y="4" width="22" height="28" rx="3" stroke="#6495ED" strokeWidth="1.2"/>
                <rect x="10" y="10" width="8" height="6" rx="1" fill="rgba(100,149,237,0.25)" stroke="#6495ED" strokeWidth="0.8"/>
                <line x1="20" y1="11" x2="26" y2="11" stroke="#6495ED" strokeWidth="1" strokeLinecap="round"/>
                <line x1="20" y1="14" x2="26" y2="14" stroke="#6495ED" strokeWidth="1" strokeLinecap="round"/>
                <line x1="10" y1="22" x2="26" y2="22" stroke="#6495ED" strokeWidth="1" strokeLinecap="round"/>
                <line x1="10" y1="26" x2="20" y2="26" stroke="#6495ED" strokeWidth="1" strokeLinecap="round"/>
                <path d="M26 26 L36 36" stroke="#6495ED" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="34" cy="34" r="5" fill="#0a0a0b" stroke="#6495ED" strokeWidth="1.2"/>
                <path d="M32 34 L33.5 35.5 L36.5 32.5" stroke="#6495ED" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="lp-step-title">Report Generation</div>
              <div className="lp-step-body">Complete the form with smart dropdowns and deficiency buttons. Pull photos from the field, add captions, generate a county-ready PDF.</div>
              <span className="lp-step-tag">Web App</span>
            </div>
          </div>
        </section>

        <div className="lp-divider" />

        {/* FEATURES */}
        <section className="lp-section" id="features">
          <div className="lp-section-label">Capabilities</div>
          <h2 className="lp-section-title lp-reveal">Built for engineers.<br />Not administrators.</h2>
          <p className="lp-section-body lp-reveal">Every feature is shaped around the real workflow of a licensed PE conducting building safety inspections.</p>
          <div className="lp-features-grid lp-reveal">
            {[
              {
                title: 'Dynamic Deficiency Library',
                body: 'One-tap blue quick-fill buttons insert pre-written deficiency language into form fields. Library grows with every project — shared across your team.',
                icon: <svg className="lp-feature-icon" viewBox="0 0 36 36" fill="none"><rect x="4" y="8" width="16" height="20" rx="3" stroke="#6495ED" strokeWidth="1"/><path d="M20 12 L32 12 M20 18 L32 18 M20 24 L28 24" stroke="#6495ED" strokeWidth="1" strokeLinecap="round"/></svg>,
              },
              {
                title: 'Real-Time Photo Sync',
                body: 'Photos taken on the iOS app upload directly to the cloud. Attach, caption, and reorder images within the web form — everything in sync.',
                icon: <svg className="lp-feature-icon" viewBox="0 0 36 36" fill="none"><rect x="8" y="4" width="14" height="20" rx="3" stroke="#6495ED" strokeWidth="1"/><circle cx="15" cy="14" r="4" stroke="#6495ED" strokeWidth="1"/><path d="M4 32 C4 24 26 24 26 32" stroke="#6495ED" strokeWidth="1" strokeLinecap="round"/><circle cx="26" cy="10" r="6" fill="#0a0a0b" stroke="#6495ED" strokeWidth="1"/><path d="M23 10 L25.5 12.5 L29 8" stroke="#6495ED" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/></svg>,
              },
              {
                title: 'County-Ready PDF Output',
                body: 'Generate BSIP-compliant PDF reports with photos, narratives, and form data — formatted to county submission standards. Electrical now; structural to follow.',
                icon: <svg className="lp-feature-icon" viewBox="0 0 36 36" fill="none"><rect x="6" y="4" width="18" height="24" rx="3" stroke="#6495ED" strokeWidth="1"/><line x1="10" y1="11" x2="20" y2="11" stroke="#6495ED" strokeWidth="1" strokeLinecap="round"/><line x1="10" y1="15" x2="20" y2="15" stroke="#6495ED" strokeWidth="1" strokeLinecap="round"/><line x1="10" y1="19" x2="16" y2="19" stroke="#6495ED" strokeWidth="1" strokeLinecap="round"/><path d="M20 22 L30 32" stroke="#6495ED" strokeWidth="1.5" strokeLinecap="round"/><rect x="22" y="24" width="10" height="10" rx="2" fill="#0a0a0b" stroke="#6495ED" strokeWidth="1"/></svg>,
              },
              {
                title: 'Staging / Production Split',
                body: 'Mirror environment architecture keeps your production photo data safe during development. Same pattern as the iOS app — consistent across the stack.',
                icon: <svg className="lp-feature-icon" viewBox="0 0 36 36" fill="none"><rect x="2" y="10" width="32" height="20" rx="3" stroke="#6495ED" strokeWidth="1"/><circle cx="18" cy="20" r="4" stroke="#6495ED" strokeWidth="1"/><path d="M10 6 L26 6" stroke="#6495ED" strokeWidth="1" strokeLinecap="round"/><path d="M14 3 L22 3" stroke="#6495ED" strokeWidth="1" strokeLinecap="round"/></svg>,
              },
              {
                title: 'Engineer-of-Record Compliant',
                body: "AI-assisted outputs are positioned as documentation and efficiency tools — not decisions. Fully compatible with Florida's PE liability framework.",
                icon: <svg className="lp-feature-icon" viewBox="0 0 36 36" fill="none"><path d="M6 28 L6 12 L18 4 L30 12 L30 28 Z" stroke="#6495ED" strokeWidth="1" strokeLinejoin="round"/><rect x="13" y="20" width="10" height="8" rx="1" stroke="#6495ED" strokeWidth="1"/><path d="M15 20 L15 17 Q15 14 18 14 Q21 14 21 17 L21 20" stroke="#6495ED" strokeWidth="1" strokeLinecap="round"/></svg>,
              },
            ].map((f, i) => (
              <div className="lp-feature-card" key={i}>
                {f.icon}
                <div className="lp-feature-title">{f.title}</div>
                <div className="lp-feature-body">{f.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* COMPLIANCE STRIP */}
        <div className="lp-compliance-strip" id="compliance">
          {[
            { title: 'Building Safety Programs', sub: 'Meets County & City Requirments' },
            { title: '40/50-Year Recertification', sub: 'Deadline tracking and submission readiness' },
            { title: 'Professional Engineer Report', sub: 'Engineer-of-record responsibilities preserved' },
          ].map((c, i) => (
            <div className="lp-compliance-item lp-reveal" key={i}>
              <div className="lp-compliance-icon-wrap">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 2 L12.8 7.6 L19 8.5 L14.5 13 L15.6 19.2 L10 16.3 L4.4 19.2 L5.5 13 L1 8.5 L7.2 7.6 Z" stroke="#6495ED" strokeWidth="1.2" strokeLinejoin="round" fill="none"/>
                </svg>
              </div>
              <div className="lp-compliance-text">
                <strong>{c.title}</strong>
                <span>{c.sub}</span>
              </div>
            </div>
          ))}
        </div>

        {/* PRICING */}
        <section className="lp-section" id="pricing">
          <div className="lp-section-label">Pricing</div>
          <h2 className="lp-section-title lp-reveal">Premium tools.<br />Premium results.</h2>
          <p className="lp-section-body lp-reveal">Built for South Florida Professional Engineers. Intentionally narrow focus, exceptional depth.</p>
          <div className="lp-tiers-grid lp-reveal">
            <div className="lp-tier-card">
              <div className="lp-tier-name">Solo</div>
              <div className="lp-tier-tagline">Single licensed engineer</div>
              <div className="lp-tier-price">Contact for pricing</div>
              <ul className="lp-tier-features">
                <li>Full BSIP electrical workflow</li>
                <li>iOS + web app access</li>
                <li>County auto-fill (all 3 counties)</li>
                <li>PDF report generation</li>
                <li>Up to 25 projects / month</li>
              </ul>
              <a href="#contact" className="lp-btn-secondary" style={{ display: 'block', textAlign: 'center' }}>Get Started</a>
            </div>
            <div className="lp-tier-card lp-featured">
              <div className="lp-tier-badge">Most Popular</div>
              <div className="lp-tier-name">Pro</div>
              <div className="lp-tier-tagline">Multi-engineer firm</div>
              <div className="lp-tier-price">Contact for pricing</div>
              <ul className="lp-tier-features">
                <li>Everything in Solo</li>
                <li>Shared deficiency library</li>
                <li>Structural module (coming soon)</li>
                <li>Multi-user roles (PE, admin, owner)</li>
                <li>Unlimited projects</li>
                <li>Priority support</li>
              </ul>
              <a href="#contact" className="lp-btn-primary" style={{ display: 'block', textAlign: 'center' }}>Get Started</a>
            </div>
            <div className="lp-tier-card">
              <div className="lp-tier-name">Ecosystem</div>
              <div className="lp-tier-tagline">PE firms + property owners</div>
              <div className="lp-tier-price">Contact for pricing</div>
              <ul className="lp-tier-features">
                <li>Everything in Pro</li>
                <li>Mac Mini hardware bundle</li>
                <li>Owner portal access</li>
                <li>White-label reporting</li>
                <li>Custom county integrations</li>
                <li>Dedicated onboarding</li>
              </ul>
              <a href="#contact" className="lp-btn-secondary" style={{ display: 'block', textAlign: 'center' }}>Contact Sales</a>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="lp-cta-section" id="contact">
          <h2 className="lp-cta-headline lp-reveal">
            Ready to<br /><span style={{ color: '#6495ED' }}>Recertify</span><br />Smarter?
          </h2>
          <p className="lp-cta-sub lp-reveal">Join the engineers transforming South Florida's building recertification workflow. Request early access today.</p>
          <div className="lp-hero-actions lp-reveal">
            <a href="mailto:info@vilanoconsulting.com" className="lp-btn-primary">Request Access</a>
            <a href="mailto:info@vilanoconsulting.com" className="lp-btn-secondary">Talk to the Team</a>
          </div>
        </div>

        {/* FOOTER */}
        <footer className="lp-footer">
          <div>
            <D2Logo width={160} height={34} />
            <p className="lp-footer-copy">© 2026 Vilano Consulting LLC. Building recertification solutions for South Florida.</p>
          </div>
          <ul className="lp-footer-links">
            <li><a href="#">Privacy</a></li>
            <li><a href="#">Terms</a></li>
            <li><a href="mailto:info@vilanoconsulting.com">Contact</a></li>
          </ul>
        </footer>

      </div>
    </>
  );
};

export default LandingPage;
